"use client";

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

type VisualKey = "finance" | "manufacturing" | "retail";

/** Finance — knowledge graph / RAG constellation */
function FinanceVisual() {
  const docs = [
    { x: 78, y: 90, w: 52, h: 64 },
    { x: 270, y: 70, w: 48, h: 58 },
    { x: 300, y: 210, w: 54, h: 66 },
    { x: 56, y: 230, w: 50, h: 60 },
  ];
  const hubs = [
    { x: 140, y: 150 },
    { x: 260, y: 155 },
    { x: 200, y: 250 },
  ];

  return (
    <svg viewBox="0 0 400 370" className="absolute inset-0 h-full w-full" aria-hidden>
      <defs>
        <radialGradient id="cs-fin-glow" cx="50%" cy="45%" r="50%">
          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="cs-fin-line" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.15" />
          <stop offset="50%" stopColor="#38bdf8" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#818cf8" stopOpacity="0.2" />
        </linearGradient>
      </defs>

      <circle cx="200" cy="175" r="130" fill="url(#cs-fin-glow)" />

      {/* Document cards */}
      {docs.map((d, i) => (
        <g key={i}>
          <rect
            x={d.x}
            y={d.y}
            width={d.w}
            height={d.h}
            rx="6"
            fill="rgba(2,11,26,0.75)"
            stroke={i % 2 ? "#38bdf8" : "#818cf8"}
            strokeWidth="1.2"
            strokeOpacity="0.7"
          />
          {[0, 1, 2].map((line) => (
            <line
              key={line}
              x1={d.x + 10}
              y1={d.y + 18 + line * 12}
              x2={d.x + d.w - 10}
              y2={d.y + 18 + line * 12}
              stroke="rgba(255,255,255,0.28)"
              strokeWidth="1.5"
            />
          ))}
          <line
            x1={d.x + d.w / 2}
            y1={d.y + d.h}
            x2="200"
            y2="175"
            stroke="url(#cs-fin-line)"
            strokeWidth="1"
          />
        </g>
      ))}

      {/* Intermediate retrieval nodes */}
      {hubs.map((h, i) => (
        <g key={i}>
          <circle cx={h.x} cy={h.y} r="11" fill="rgba(2,11,26,0.8)" stroke="#38bdf8" strokeWidth="1.3" />
          <circle cx={h.x} cy={h.y} r="3.5" fill={i === 1 ? "#fb923c" : "#38bdf8"} />
        </g>
      ))}

      {/* Central RAG core */}
      <circle cx="200" cy="175" r="42" fill="rgba(2,11,26,0.7)" stroke="rgba(255,255,255,0.45)" strokeWidth="1.5" />
      <circle cx="200" cy="175" r="22" fill="none" stroke="#3b82f6" strokeWidth="1" strokeDasharray="3 5" />
      <circle cx="200" cy="175" r="8" fill="#3b82f6" />
      <text x="200" y="230" textAnchor="middle" fill="rgba(255,255,255,0.75)" fontSize="10" letterSpacing="0.2em">
        RAG
      </text>
      <text x="200" y="248" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="8" letterSpacing="0.12em">
        KNOWLEDGE GRAPH
      </text>
    </svg>
  );
}

/** Manufacturing — plant pipeline / sensor flow */
function ManufacturingVisual() {
  const stations = [
    { x: 48, label: "RAW" },
    { x: 128, label: "LINE" },
    { x: 208, label: "SENSE" },
    { x: 288, label: "PREDICT" },
  ];

  return (
    <svg viewBox="0 0 400 370" className="absolute inset-0 h-full w-full" aria-hidden>
      <defs>
        <radialGradient id="cs-mfg-glow" cx="55%" cy="50%" r="55%">
          <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#f59e0b" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="cs-mfg-belt" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.2" />
          <stop offset="50%" stopColor="#f59e0b" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.25" />
        </linearGradient>
      </defs>

      <ellipse cx="210" cy="190" rx="160" ry="100" fill="url(#cs-mfg-glow)" />

      {/* Grid / plant floor */}
      {[0, 1, 2, 3, 4].map((row) =>
        [0, 1, 2, 3, 4, 5].map((col) => (
          <circle
            key={`${row}-${col}`}
            cx={70 + col * 48}
            cy={80 + row * 42}
            r="1.2"
            fill="rgba(255,255,255,0.18)"
          />
        )),
      )}

      {/* Conveyor belt */}
      <path
        d="M40 200 H360"
        fill="none"
        stroke="url(#cs-mfg-belt)"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <path
        d="M40 212 H360"
        fill="none"
        stroke="rgba(255,255,255,0.12)"
        strokeWidth="1"
        strokeDasharray="6 8"
      />

      {/* Process stations */}
      {stations.map((s, i) => (
        <g key={s.label}>
          <rect
            x={s.x}
            y={152}
            width="56"
            height="56"
            rx="10"
            fill="rgba(2,11,26,0.8)"
            stroke={i === 3 ? "#f59e0b" : "#22d3ee"}
            strokeWidth="1.4"
          />
          <circle
            cx={s.x + 28}
            cy={180}
            r="8"
            fill={i % 2 ? "#f59e0b" : "#22d3ee"}
            fillOpacity="0.9"
          />
          {/* Signal bars */}
          {[0, 1, 2].map((b) => (
            <rect
              key={b}
              x={s.x + 14 + b * 10}
              y={210 - (b + 1) * 6}
              width="6"
              height={(b + 1) * 6}
              rx="1"
              fill="rgba(255,255,255,0.35)"
            />
          ))}
          <text
            x={s.x + 28}
            y={240}
            textAnchor="middle"
            fill="rgba(255,255,255,0.65)"
            fontSize="8"
            letterSpacing="0.14em"
          >
            {s.label}
          </text>
          {i < stations.length - 1 ? (
            <path
              d={`M${s.x + 56} 180 H${stations[i + 1].x}`}
              stroke="#22d3ee"
              strokeOpacity="0.45"
              strokeWidth="1.2"
            />
          ) : null}
        </g>
      ))}

      {/* Telemetry arc */}
      <path
        d="M70 120 C140 70, 260 70, 330 130"
        fill="none"
        stroke="#f59e0b"
        strokeOpacity="0.4"
        strokeWidth="1"
        strokeDasharray="4 6"
      />
      <circle cx="200" cy="88" r="5" fill="#f59e0b" />
      <text x="200" y="78" textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="8" letterSpacing="0.16em">
        LIVE TELEMETRY
      </text>

      <text x="200" y="290" textAnchor="middle" fill="rgba(255,255,255,0.75)" fontSize="10" letterSpacing="0.2em">
        DATA PIPELINE
      </text>
      <text x="200" y="308" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="8" letterSpacing="0.12em">
        PLANT → PREDICT
      </text>
    </svg>
  );
}

/** Retail — customer journey / CX mesh */
function RetailVisual() {
  const channels = [
    { x: 200, y: 58, label: "WEB" },
    { x: 320, y: 130, label: "APP" },
    { x: 300, y: 260, label: "STORE" },
    { x: 100, y: 260, label: "SUPPORT" },
    { x: 80, y: 130, label: "CRM" },
  ];

  return (
    <svg viewBox="0 0 400 370" className="absolute inset-0 h-full w-full" aria-hidden>
      <defs>
        <radialGradient id="cs-ret-glow" cx="50%" cy="48%" r="50%">
          <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.4" />
          <stop offset="60%" stopColor="#fb7185" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#a78bfa" stopOpacity="0" />
        </radialGradient>
      </defs>

      <circle cx="200" cy="175" r="125" fill="url(#cs-ret-glow)" />

      {/* Concentric journey rings — asymmetric */}
      <ellipse
        cx="200"
        cy="175"
        rx="135"
        ry="115"
        fill="none"
        stroke="rgba(255,255,255,0.16)"
        strokeWidth="1"
      />
      <ellipse
        cx="200"
        cy="175"
        rx="95"
        ry="78"
        fill="none"
        stroke="#a78bfa"
        strokeOpacity="0.35"
        strokeWidth="1"
        strokeDasharray="5 9"
      />
      <path
        d="M90 175 Q200 95 310 175 Q200 255 90 175"
        fill="none"
        stroke="#fb7185"
        strokeOpacity="0.35"
        strokeWidth="1.2"
      />

      {/* Channel nodes */}
      {channels.map((c, i) => (
        <g key={c.label}>
          <line
            x1="200"
            y1="175"
            x2={c.x}
            y2={c.y}
            stroke={i % 2 ? "#fb7185" : "#a78bfa"}
            strokeOpacity="0.35"
            strokeWidth="1"
          />
          <circle
            cx={c.x}
            cy={c.y}
            r="20"
            fill="rgba(2,11,26,0.8)"
            stroke={i % 2 ? "#fb7185" : "#38bdf8"}
            strokeWidth="1.4"
          />
          <circle cx={c.x} cy={c.y} r="4" fill={i % 2 ? "#fb7185" : "#38bdf8"} />
          <text
            x={c.x}
            y={c.y + 34}
            textAnchor="middle"
            fill="rgba(255,255,255,0.7)"
            fontSize="8"
            letterSpacing="0.12em"
          >
            {c.label}
          </text>
        </g>
      ))}

      {/* Center CX orchestrator */}
      <circle cx="200" cy="175" r="36" fill="rgba(2,11,26,0.75)" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" />
      <circle cx="200" cy="175" r="14" fill="none" stroke="#a78bfa" strokeWidth="1.5" />
      <circle cx="200" cy="175" r="5" fill="#fb7185" />
      <text x="200" y="292" textAnchor="middle" fill="rgba(255,255,255,0.75)" fontSize="10" letterSpacing="0.2em">
        CX MESH
      </text>
      <text x="200" y="310" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="8" letterSpacing="0.12em">
        JOURNEY ORCHESTRATION
      </text>
    </svg>
  );
}

const visuals: Record<VisualKey, () => ReactNode> = {
  finance: FinanceVisual,
  manufacturing: ManufacturingVisual,
  retail: RetailVisual,
};

const atmospheres: Record<VisualKey, string> = {
  finance: "bg-[radial-gradient(ellipse_at_center,rgba(59,130,246,0.28),transparent_65%)]",
  manufacturing: "bg-[radial-gradient(ellipse_at_60%_50%,rgba(34,211,238,0.22),transparent_55%),radial-gradient(ellipse_at_30%_70%,rgba(245,158,11,0.14),transparent_50%)]",
  retail: "bg-[radial-gradient(ellipse_at_center,rgba(167,139,250,0.26),transparent_60%),radial-gradient(ellipse_at_70%_40%,rgba(251,113,133,0.12),transparent_50%)]",
};

export function CaseStudyVisual({
  variant,
  industry,
  client,
  className,
}: {
  variant: VisualKey;
  industry: string;
  client: string;
  className?: string;
}) {
  const Graphic = visuals[variant];

  return (
    <div className={cn("relative min-h-[280px] overflow-hidden bg-[#020b1a] sm:min-h-[320px]", className)}>
      <div className={cn("absolute inset-0", atmospheres[variant])} />
      <div className="absolute inset-0 opacity-25 [background-image:radial-gradient(rgba(255,255,255,0.12)_1px,transparent_1px)] [background-size:22px_22px]" />
      <Graphic />
      <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[#020b1a] via-[#020b1a]/80 to-transparent" />
      <div className="absolute bottom-5 left-5 right-5 z-10 sm:bottom-6 sm:left-6">
        <p className="text-[11px] uppercase tracking-[0.18em] text-white/60">{industry}</p>
        <p className="mt-1.5 font-display text-xl tracking-tight text-white sm:text-2xl">{client}</p>
      </div>
    </div>
  );
}

export function caseStudyVisualKey(industry: string): VisualKey {
  if (industry.toLowerCase().includes("manufactur")) return "manufacturing";
  if (industry.toLowerCase().includes("retail")) return "retail";
  return "finance";
}
